// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// WB-007 — anchoring an annotation overlay to a shared-screen <video>.
//
// A screen-share video is rendered with `object-fit: contain`, so it's letterboxed:
// the actual picture occupies a centered sub-rectangle of the element, with black bars
// on two sides. To make a drawn mark land on the SAME content pixel for every viewer —
// regardless of their window size — the overlay canvas must be sized and positioned to
// that content sub-rectangle, and coordinates normalized to the video's INTRINSIC
// resolution (not the rendered element size). The engine's own normalization then maps
// pointer ↔ intrinsic correctly, because the canvas IS the content box.
//
// This module is the pure geometry; the React overlay (AnnotationOverlay) applies it.

import type { Size } from "./types";

/** The letterboxed content rectangle, in element-local CSS pixels. */
export interface ContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Compute where a video's picture actually sits inside an `object-fit: contain`
 * element. Returns the centered content rectangle; black bars are the remainder.
 * Degenerate inputs (zero intrinsic or element size) collapse to a zero rect at the
 * origin rather than producing NaN/Infinity.
 */
export function videoContentRect(intrinsic: Size, element: Size): ContentRect {
  if (intrinsic.width <= 0 || intrinsic.height <= 0 || element.width <= 0 || element.height <= 0) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  const scale = Math.min(element.width / intrinsic.width, element.height / intrinsic.height);
  const width = intrinsic.width * scale;
  const height = intrinsic.height * scale;
  return {
    left: (element.width - width) / 2,
    top: (element.height - height) / 2,
    width,
    height,
  };
}
